/**
 * The `@` picker's candidates (DECISIONS #95; owner: "They should also be
 * able to tag with @ just like how I can tag files in Conductor with @").
 *
 * Typing `@` in the composer opens a list of things the reader can name:
 * the parts of the drawing on the pane, the product's flows, the areas of
 * the code, its runtime entities (services, queues, stores, schedules) and
 * its files. Picking one adds a chip beside the question. A part becomes
 * part of the picked set (the same chips the drawing's clicks make); the
 * rest ride the send as `mentions`, and the server fetches each one's
 * record into the turn before the question so the guide starts from it.
 *
 * Pure: the sources are plain arrays the panel builds from what it already
 * has, and the ranking is one function a test can read.
 */

import type { MessageMention } from '../../api/sse';
import type { DiagramPart } from './transcript';

export type MentionKind = 'part' | MessageMention['kind'];

export interface MentionCandidate {
  /** unique across kinds: `${kind}:${id}` */
  key: string;
  kind: MentionKind;
  /** the flow slug, node id, entity id, path; a part's label */
  id: string;
  /** what the chip will say */
  label: string;
  /** one quiet line under it: what the thing is */
  hint: string | null;
}

export interface MentionSources {
  parts: readonly DiagramPart[];
  flows: ReadonlyArray<{ id: string; title: string; hint: string | null }>;
  areas: ReadonlyArray<{ id: string; hint: string | null }>;
  entities: ReadonlyArray<{ id: string; label: string; kind: string }>;
  files: readonly string[];
}

export const EMPTY_SOURCES: MentionSources = Object.freeze({
  parts: [],
  flows: [],
  areas: [],
  entities: [],
  files: [],
});

/** How many candidates the picker lists at once. */
export const MENTION_LIMIT = 8;

/** The word the chip and the picker put in front of a candidate's name. */
export const KIND_WORD: Record<MentionKind, string> = {
  part: 'part',
  flow: 'flow',
  area: 'area',
  entity: 'runtime',
  file: 'file',
};

function candidatesOf(sources: MentionSources): MentionCandidate[] {
  const out: MentionCandidate[] = [];
  for (const part of sources.parts) {
    out.push({ key: `part:${part.label}`, kind: 'part', id: part.label, label: part.label, hint: part.what });
  }
  for (const flow of sources.flows) {
    out.push({ key: `flow:${flow.id}`, kind: 'flow', id: flow.id, label: flow.title, hint: flow.hint });
  }
  for (const area of sources.areas) {
    out.push({ key: `area:${area.id}`, kind: 'area', id: area.id, label: area.id, hint: area.hint });
  }
  for (const entity of sources.entities) {
    out.push({
      key: `entity:${entity.id}`,
      kind: 'entity',
      id: entity.id,
      label: entity.label,
      hint: entity.kind,
    });
  }
  for (const file of sources.files) {
    const name = file.split('/').pop() ?? file;
    out.push({ key: `file:${file}`, kind: 'file', id: file, label: name, hint: file });
  }
  return out;
}

/**
 * How well a candidate answers the query: 3 for a name that starts with it,
 * 2 for a word in the name that does, 1 for the query anywhere in the name
 * or the id, 0 for no match. An empty query matches everything at 1.
 */
export function mentionScore(candidate: MentionCandidate, query: string): number {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return 1;
  const label = candidate.label.toLowerCase();
  if (label.startsWith(q)) return 3;
  if (label.split(/[\s/_.-]+/).some((word) => word.startsWith(q))) return 2;
  if (label.includes(q) || candidate.id.toLowerCase().includes(q)) return 1;
  return 0;
}

/**
 * The candidates for `query`, best first, at most MENTION_LIMIT. Ties keep
 * the source order: the parts of the drawing on screen first, then flows,
 * areas, runtime entities and files, which is the order a reader most
 * likely means them in.
 */
export function mentionCandidates(query: string, sources: MentionSources): MentionCandidate[] {
  const scored = candidatesOf(sources)
    .map((candidate, index) => ({ candidate, index, score: mentionScore(candidate, query) }))
    .filter((entry) => entry.score > 0);
  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored.slice(0, MENTION_LIMIT).map((entry) => entry.candidate);
}

/** The wire form of a non-part candidate (a part joins the picked set instead). */
export function mentionToWire(candidate: MentionCandidate, repoId: string): MessageMention | null {
  if (candidate.kind === 'part') return null;
  return { kind: candidate.kind, repo_id: repoId, id: candidate.id, label: candidate.label };
}

/**
 * The `@` token the caret is in, if any: `@` at the start of the text or
 * after whitespace, followed by anything but whitespace, up to the caret.
 * Null when the caret is not inside such a token.
 */
export function mentionTokenAt(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret);
  const match = /(^|\s)@([^\s@]*)$/.exec(upto);
  if (match === null) return null;
  const start = upto.length - match[0].length + (match[1] ?? '').length;
  return { start, query: match[2] ?? '' };
}

/**
 * The inline form (DECISIONS #96; owner: "The @ should be inline like
 * Conductor"): a named thing sits IN the text as `@Label`, drawn as a pill
 * by the composer's backdrop. `known` is every candidate the reader has
 * inserted this session (label -> candidate); the tokens present in `text`
 * are the ones whose `@Label` is still there, longest labels matched first
 * so "@Daily run" is never read as "@Daily". In text order, deduplicated.
 */
export function tokensIn(
  text: string,
  known: ReadonlyMap<string, MentionCandidate>,
): MentionCandidate[] {
  const labels = [...known.keys()].sort((a, b) => b.length - a.length);
  const found: Array<{ at: number; candidate: MentionCandidate }> = [];
  const taken: Array<[number, number]> = [];
  for (const label of labels) {
    const candidate = known.get(label);
    if (candidate === undefined) continue;
    const needle = `@${label}`;
    let from = 0;
    while (from <= text.length) {
      const at = text.indexOf(needle, from);
      if (at === -1) break;
      const end = at + needle.length;
      const before = at === 0 ? ' ' : text[at - 1];
      const after = end >= text.length ? ' ' : text[end];
      const overlaps = taken.some(([a, b]) => at < b && end > a);
      // A token starts the text or follows whitespace, and ends the text or
      // is followed by something that is not a letter (so "@api" inside
      // "@api-prod" does not fire when both are known).
      if (!overlaps && /\s/.test(before ?? ' ') && !/[A-Za-z0-9_]/.test(after ?? ' ')) {
        taken.push([at, end]);
        if (!found.some((f) => f.candidate.key === candidate.key)) found.push({ at, candidate });
      }
      from = end;
    }
  }
  return found.sort((a, b) => a.at - b.at).map((f) => f.candidate);
}

/** Spans of `text`: plain runs and the tokens in them, for the backdrop. */
export function tokenSpans(
  text: string,
  known: ReadonlyMap<string, MentionCandidate>,
): Array<{ text: string; token: MentionCandidate | null }> {
  const labels = [...known.keys()].sort((a, b) => b.length - a.length);
  const spans: Array<{ text: string; token: MentionCandidate | null }> = [];
  let i = 0;
  let plain = '';
  while (i < text.length) {
    let hit: MentionCandidate | null = null;
    let hitLen = 0;
    if (text[i] === '@' && (i === 0 || /\s/.test(text[i - 1] ?? ' '))) {
      for (const label of labels) {
        const needle = `@${label}`;
        if (text.startsWith(needle, i)) {
          const after = text[i + needle.length];
          if (after === undefined || !/[A-Za-z0-9_]/.test(after)) {
            hit = known.get(label) ?? null;
            hitLen = needle.length;
            break;
          }
        }
      }
    }
    if (hit !== null) {
      if (plain.length > 0) spans.push({ text: plain, token: null });
      plain = '';
      spans.push({ text: text.slice(i, i + hitLen), token: hit });
      i += hitLen;
    } else {
      plain += text[i];
      i += 1;
    }
  }
  if (plain.length > 0) spans.push({ text: plain, token: null });
  return spans;
}

/**
 * `text` with `@Label ` put in at `caret` (a space before it when the
 * caret follows a letter, one after it always), and where the caret lands.
 */
export function insertToken(
  text: string,
  caret: number,
  label: string,
): { text: string; caret: number } {
  const at = Math.max(0, Math.min(caret, text.length));
  const before = text.slice(0, at);
  const after = text.slice(at);
  const lead = before.length > 0 && !/\s$/.test(before) ? ' ' : '';
  const token = `${lead}@${label} `;
  return { text: before + token + after, caret: before.length + token.length };
}

/**
 * `text` with every `@Label` token for `label` taken out. `known` (the
 * other labels in play) keeps a longer token whole: with "Daily run" known,
 * removing "Daily" leaves "@Daily run" alone.
 */
export function removeToken(
  text: string,
  label: string,
  known: ReadonlyMap<string, MentionCandidate> = new Map(),
): string {
  const needle = `@${label}`;
  const longer = [...known.keys()].filter((k) => k.length > label.length && k.startsWith(label));
  let out = '';
  let i = 0;
  while (i < text.length) {
    if (
      text.startsWith(needle, i) &&
      (i === 0 || /\s/.test(text[i - 1] ?? ' ')) &&
      !longer.some((k) => text.startsWith(`@${k}`, i))
    ) {
      const after = text[i + needle.length];
      if (after === undefined || !/[A-Za-z0-9_]/.test(after)) {
        i += needle.length;
        if (text[i] === ' ') i += 1; // and the space that followed it
        continue;
      }
    }
    out += text[i];
    i += 1;
  }
  return out;
}
